import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { MAIN_APP_URL } from "../../lib/config";

const NAV_LINKS = [
  { href: "/#what-we-do", label: "What we do" },
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#for-carriers", label: "For carriers" },
  { href: "/#for-customers", label: "For customers" },
  { href: "/faq", label: "FAQ" },
  { href: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-cream-50/90 backdrop-blur-md border-b border-moss-100 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <a
          href="/"
          className="flex items-center gap-2 group"
          data-testid="navbar-logo-link"
        >
          <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center">
            <Logo className="w-full h-full drop-shadow-sm" />
          </span>
          <span className="font-display text-xl md:text-2xl font-bold tracking-tight text-moss-950">
            Binshare
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm font-medium text-moss-800 hover:text-sage-700 transition-colors"
              data-testid={`navbar-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={MAIN_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex"
            data-testid="navbar-post-job-link"
          >
            <Button
              className="bg-moss-900 hover:bg-moss-950 text-cream-50 rounded-full px-5 h-10 shadow-sm"
              data-testid="navbar-post-job-btn"
            >
              Post a job
            </Button>
          </a>
          <button
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-moss-900 hover:bg-cream-100"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden bg-cream-50 border-t border-moss-100"
          data-testid="navbar-mobile-menu"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="py-2 text-base font-medium text-moss-800 hover:text-sage-700"
                data-testid={`navbar-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={MAIN_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              className="mt-3 w-full"
              data-testid="navbar-mobile-post-job-link"
            >
              <Button
                className="w-full bg-moss-900 hover:bg-moss-950 text-cream-50 rounded-full"
                data-testid="navbar-mobile-post-job-btn"
              >
                Post a job
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
